import { useNavigate, useParams } from 'react-router';
import { toast } from 'sonner';

import { AuthenticatedRoute } from '@/app/constant';
import {
  classifyAssetStatus,
  getActionApplicability,
} from '@/components/actions';
import { AskMarcusButton } from '@/components/actions/AskMarcusButton';
import { getHardDeleteDisabledReason } from '@/components/actions/constants/hardDelete';
import { HardDeleteTrigger } from '@/components/actions/HardDeleteTrigger';
import { SummonConstantineButton } from '@/components/actions/SummonConstantineButton';
import {
  buildAnnotateAction,
  buildExportAction,
  buildGenericCustomAction,
  buildImportAction,
  buildManageAction,
  buildScanActionOptions,
  buildShareAction,
  TableActionsBar,
} from '@/components/table/actions';
import { useAxios } from '@/hooks/useAxios';
import {
  shouldUseBulk,
  useBulkAction,
} from '@/hooks/useBulkAction/useBulkAction';
import { BulkSelectionState } from '@/hooks/useBulkSelection/types';
import { useEntitlements } from '@/hooks/useEntitlements';
import { useScanProfiles } from '@/hooks/useScanProfiles';
import { useSummonAurelius } from '@/hooks/useSummonAurelius';
import { queryClient } from '@/queryclient';
import { getAssetScanOptions } from '@/sections/asset/config/actionOptions';
import { useAssetActionHandlers } from '@/sections/asset/hooks/actions';
import { useAuth } from '@/state/auth';
import { useGlobalState } from '@/state/global.state';
import { Asset, AssetStatus } from '@/types';
import { runChunked } from '@/utils/runChunked';

interface AssetActionsProps {
  /** Assets to perform actions on */
  assets: Asset[];
  /** Callback for successful actions (legacy, unused) */
  onSuccess?: () => void;
  /** Additional CSS classes */
  className?: string;
  /** Hide the "Actions:" label */
  hideLabel?: boolean;
  /** Handler for opening tag modal */
  onTagClick?: () => void;
  /** Handler for opening comment modal */
  onCommentClick?: () => void;
  /** Handler for opening remove tag modal */
  onRemoveTagClick?: () => void;
  /** Handler for opening import modal (always enabled) */
  onImportClick?: () => void;
  /** Handler for opening export modal (always enabled) */
  onExportClick: () => void;
  /** Hide all action buttons */
  hideActions?: boolean;
  /** Disable actions when no items selected */
  disabled?: boolean;
  /** Handler for opening Constantine repo modal (only shown for repositories) */
  onConstantineClick?: () => void;
  /**
   * The current bulk-selection state (from useBulkSelection in the parent).
   * When provided alongside onRemoveProfileBulk, large or 'all'-mode selections
   * open the BulkConfirmationDialog instead of mutating directly.
   */
  selectionState?: BulkSelectionState;
  /**
   * Callback that opens the BulkConfirmationDialog for remove_profile.
   * Provided by the parent (sections/asset/index.tsx) via removeProfileConfirm.openConfirmation.
   */
  onRemoveProfileBulk?: () => void;
  /**
   * Callback that opens the credential management modal for a single web app asset.
   * Provided as a direct prop (not on AssetActionHandlers) per F13 resolution.
   */
  onCredentialsClick?: () => void;
  /**
   * Callback that opens the bulk permanent-delete (hard delete) confirmation.
   * Praetorian-only, explicit-selection only. When provided and the user is a
   * Praetorian user (and selection is not "all matching"), a "Delete Permanently"
   * action is shown. The backend caps bulk hard delete at 1000 keys — the button
   * is disabled above that count.
   */
  onHardDeleteBulk?: () => void;
  /**
   * Callback that opens the BulkConfirmationDialog for no-ai tag/untag.
   * Called with `adding: true` to add the tag, `false` to remove it.
   */
  onNoAiBulk?: (adding: boolean) => void;
  /**
   * Opens the status_change BulkConfirmationDialog (freeze/activate/soft-delete).
   * Without it, large/'all'-mode status changes fall back to per-item PUTs over
   * only the loaded rows — see useAssetStatusHandlers bulk-path gate.
   */
  onStatusChangeBulk?: (status: AssetStatus) => void;
}

export function AssetActions({
  assets,
  className,
  hideLabel,
  onCommentClick,
  onTagClick,
  onRemoveTagClick,
  onImportClick,
  onExportClick,
  hideActions,
  disabled = false,
  onConstantineClick,
  selectionState,
  onRemoveProfileBulk,
  onCredentialsClick,
  onHardDeleteBulk,
  onNoAiBulk,
  onStatusChangeBulk,
}: AssetActionsProps) {
  const handlers = useAssetActionHandlers({
    selectionState,
    onBulkRequested: onStatusChangeBulk,
  });
  const {
    modal: { selectUserAgent },
  } = useGlobalState();
  const { isPraetorianUser } = useAuth();
  const { summonAurelius } = useSummonAurelius();
  const { canWriteAssets, canWriteJobs } = useEntitlements();
  const { profiles } = useScanProfiles();
  const axios = useAxios();
  const navigate = useNavigate();
  const { userId } = useParams();

  // useBulkAction for remove_profile — used when no dialog confirmation is needed
  // (1-2 explicit assets without selectionState/onRemoveProfileBulk, or fallback).
  // For 3+ assets or 'all' mode with onRemoveProfileBulk provided, the parent's
  // BulkConfirmationDialog is opened instead (see handleRemoveProfile below).
  const submitRemoveProfileBulk = useBulkAction({
    resource: 'asset',
    action: 'remove_profile',
  });

  // Compute selection state for smart-context scan profile menu
  const withProfileCount = assets.filter(a => !!a.profile).length;
  const scanProfileSelectionState =
    withProfileCount === 0
      ? ('none' as const)
      : withProfileCount === assets.length
        ? ('all' as const)
        : ('mixed' as const);

  // Run PUT requests for each target and return failed keys.
  const runAssetPuts = async (
    targets: Asset[],
    buildBody: (asset: Asset) => Record<string, unknown>
  ): Promise<Set<string>> => {
    const result = await runChunked(
      targets,
      asset => axios.put('/asset', buildBody(asset)),
      { concurrency: 8 }
    );
    return new Set(result.failures.map(f => f.item.key));
  };

  // Add a profile only to assets that currently have none
  const handleAddProfile = async (profileId: string) => {
    const targets = assets.filter(a => !a.profile);
    if (targets.length === 0) return;
    const failedKeys = await runAssetPuts(targets, asset => ({
      key: asset.key,
      profile: profileId,
    }));
    const succeededKeys = new Set(
      targets.filter(t => !failedKeys.has(t.key)).map(t => t.key)
    );
    queryClient.setQueriesData(
      {
        predicate: query => {
          const qk = query.queryKey as string[];
          return qk[0] === 'MY' && qk[1] === 'asset';
        },
      },
      (old: unknown) => {
        if (!old || typeof old !== 'object') return old;
        const data = old as { pages?: { data: Asset[] }[] };
        if (!data.pages) return old;
        return {
          ...data,
          pages: data.pages.map(page => ({
            ...page,
            data: page.data.map((a: Asset) =>
              succeededKeys.has(a.key) ? { ...a, profile: profileId } : a
            ),
          })),
        };
      }
    );
    await queryClient.invalidateQueries({
      predicate: query => {
        const qk = query.queryKey as string[];
        return qk[0] === 'MY' && qk[1] === 'asset';
      },
    });
    if (failedKeys.size > 0) {
      toast.error(
        `Failed to assign scan profile for ${failedKeys.size} asset${failedKeys.size === 1 ? '' : 's'}`
      );
    } else {
      toast.success('Scan profile assigned');
    }
  };

  // Remove profile from assets that currently have one.
  // Routes ALL clears through the bulk endpoint — per-item PUT /asset {profile: ''} is
  // broken (BaseAsset.MergeFields skips empty-string Profile assignments).
  // See feedback_bulk_confirm_bypass_pattern.md and architecture-frontend.md §1b.
  const handleRemoveProfile = () => {
    const targets = assets.filter(a => !!a.profile);
    if (targets.length === 0) return;

    // 3+ assets or 'all' mode: delegate to BulkConfirmationDialog via parent.
    // shouldUseBulk returns true for remove_profile unconditionally (PUT path is broken).
    if (
      selectionState &&
      shouldUseBulk(selectionState, 'remove_profile') &&
      onRemoveProfileBulk
    ) {
      onRemoveProfileBulk();
      return;
    }

    // 1–2 explicit assets (or selectionState/onRemoveProfileBulk not provided by caller):
    // still issue an explicit-mode bulk POST — no dialog needed at small N.
    // useBulkAction.onSuccess fires the "Remove profile submitted" toast and
    // registers the job in bulkJobs.store. No optimistic setQueriesData here —
    // useActiveBulkJobs owns cache invalidation on terminal job state.
    submitRemoveProfileBulk.mutate({
      action: 'remove_profile',
      params: {
        profile_ids: Array.from(
          new Set(targets.map(t => t.profile).filter(Boolean))
        ),
      },
      selection: { mode: 'explicit', keys: targets.map(t => t.key) },
    });
  };

  const handleManageProfiles = () => {
    navigate(`/${userId}/${AuthenticatedRoute.SETTINGS_SCAN}`);
  };

  const NO_AI_TAG = 'no-ai';
  const allHaveNoAi = assets.every(a => a.tags?.includes(NO_AI_TAG));

  const submitNoAiTag = useBulkAction({ resource: 'asset', action: 'tag' });
  const submitNoAiUntag = useBulkAction({
    resource: 'asset',
    action: 'untag',
  });

  const handleToggleNoAi = () => {
    if (assets.length === 0) return;

    const adding = !allHaveNoAi;

    if (selectionState && shouldUseBulk(selectionState) && onNoAiBulk) {
      onNoAiBulk(adding);
      return;
    }

    const submit = adding ? submitNoAiTag : submitNoAiUntag;
    submit.mutate({
      action: adding ? 'tag' : 'untag',
      params: { tag: NO_AI_TAG },
      selection: { mode: 'explicit', keys: assets.map(a => a.key) },
    });
  };

  // Build dropdown options using option builders
  const scanOptions = getAssetScanOptions({
    assets,
    handlers,
    selectUserAgent,
    isPraetorianUser,
  });

  // State-aware gating: hide the Freeze/Unfreeze no-op by current state. Assets
  // have no Approve/Reject verbs, so only the freeze toggle is affected. Mixed
  // selections stay permissive; select-all-matching short-circuits to permissive.
  const applicability = getActionApplicability(
    assets.map(a => classifyAssetStatus(a.status)),
    selectionState?.mode === 'all'
  );

  // Build action configurations
  const actions = [
    buildGenericCustomAction({
      render: () => (
        <AskMarcusButton
          onClick={() =>
            summonAurelius({
              type: 'asset',
              keys: assets.map(a => a.key),
              displayNames: assets.map(a => a.dns || a.name),
            })
          }
          disabled={disabled}
          data-testid="asset-ask-marcus-button"
        />
      ),
      position: 'first',
      testId: 'asset-ask-marcus-wrapper',
    }),

    ...(onConstantineClick
      ? [
          buildGenericCustomAction({
            render: () => (
              <SummonConstantineButton
                onClick={onConstantineClick}
                disabled={disabled}
                data-testid="asset-summon-constantine-button"
              />
            ),
            position: 'first',
            testId: 'asset-summon-constantine-wrapper',
          }),
        ]
      : []),

    ...(canWriteAssets
      ? [
          buildAnnotateAction({
            onCommentClick: onCommentClick || (() => {}),
            onTagClick: onTagClick || (() => {}),
            onRemoveTagClick: onRemoveTagClick || (() => {}),
            labels: { comment: 'Add Note' },
            scanProfile: {
              profiles,
              selectionState: scanProfileSelectionState,
              onAdd: handleAddProfile,
              onRemove: handleRemoveProfile,
              onManage: handleManageProfiles,
            },
            testId: 'asset-annotate-dropdown',
          }),
        ]
      : []),

    ...(canWriteJobs
      ? [
          buildScanActionOptions({
            scanOptions,
            testId: 'asset-scan-dropdown',
          }),
        ]
      : []),

    buildShareAction({
      variant: 'button-only',
      onShareClick: () => handlers.handleShare(assets),
      testId: 'asset-share-button',
    }),

    ...(canWriteAssets
      ? [
          buildManageAction({
            onFreezeClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Frozen),
            onActivateClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Active),
            onDeleteClick: () =>
              handlers.handleUpdateStatus(assets, AssetStatus.Deleted),
            onNoAiClick: handleToggleNoAi,
            noAiLabel: allHaveNoAi ? 'Enable AI' : 'Disable AI',
            onCredentialsClick,
            shortcuts: {
              freeze: ' ⌥ F',
              activate: ' ⌥ U',
              delete: ' ⌥ D',
            },
            hideActions: {
              freeze: applicability.hideFreeze,
              activate: applicability.hideUnfreeze,
            },
            testId: 'asset-managed-dropdown',
          }),
        ]
      : []),

    // Praetorian-only permanent (hard) delete. Stays VISIBLE for Praetorian
    // users; becomes DISABLED (with a keyboard/AT-reachable reason) for
    // "select all matching" or selections over the backend cap — never
    // hidden, never error-after-click. Reason copy is centralized in
    // getHardDeleteDisabledReason so it can't drift across entities.
    ...(isPraetorianUser && onHardDeleteBulk && canWriteAssets
      ? [
          buildGenericCustomAction({
            render: () => (
              <HardDeleteTrigger
                disabled={disabled || assets.length === 0}
                disabledReason={getHardDeleteDisabledReason({
                  isSelectAll: selectionState?.mode === 'all',
                  selectedCount: assets.length,
                })}
                onClick={onHardDeleteBulk}
                data-testid="asset-hard-delete-button"
              />
            ),
            testId: 'asset-hard-delete-wrapper',
          }),
        ]
      : []),

    ...(onImportClick && canWriteAssets
      ? [
          buildImportAction({
            onImportClick,
            disabled: false, // Always enabled
            testId: 'asset-import-button',
          }),
        ]
      : []),

    buildExportAction({
      onExportClick,
      disabled: disabled,
      alwaysEnabled: false,
      testId: 'asset-export-button',
    }),
  ];

  return (
    <TableActionsBar
      selectedItems={assets}
      actions={actions}
      disabled={disabled}
      className={className}
      hideLabel={hideLabel}
      hideActions={hideActions}
      testIdPrefix="asset"
    />
  );
}
